import React, { useCallback, useContext, useEffect, useState } from "react";
import config from "../../data/config";
import {
  WhmGrid,
  WhmSkeleton,
  WhmStack,
  WhmTab,
  WhmTabs,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Information } from "./information/Information";
import { ReactComponent as PersonalInformationIcon } from "@securitize/assets-sec-ui/dist/icons/svg/entity-info.svg";
import { ReactComponent as TaxIcon } from "@securitize/assets-sec-ui/dist/icons/svg/tax.svg";
import { ReactComponent as DocumentsIcon } from "@securitize/assets-sec-ui/dist/icons/svg/document.svg";
import { ReactComponent as DocumentCenterIcon } from "@securitize/assets-sec-ui/dist/icons/svg/additional-info.svg";
import { ReactComponent as InvestmentProfileIcon } from "@securitize/assets-sec-ui/dist/icons/svg/acreditation.svg";
import { ReactComponent as SettingsIcon } from "@securitize/assets-sec-ui/dist/icons/svg/settings.svg";
import { ReactComponent as MembersIcon } from "@securitize/assets-sec-ui/dist/icons/svg/members.svg";
import { ReactComponent as KeyPartiesIcon } from "@securitize/assets-sec-ui/dist/icons/svg/key-parties.svg";
import { Translate } from "../Translation/Translate";
import TranslationKeys from "../../contexts/I18nContext/translationKeys";
import useUserInfo from "../../hooks/useUserInfo";
import { useNavigate, useParams } from "react-router";
import {
  profilePaths,
  tabRoutes,
  tabRoutesToProfilePaths,
} from "./routing-constants";
import { InvestmentProfile } from "./investment-profile/InvestmentProfile";
import {
  useGetConfigQuery,
  useGetDocumentsQuery,
  useGetInvestmentProfileQuery,
  useGetInvestorInformationQuery,
  useGetKeyPartiesQuery,
  useGetPendingActionsQuery,
  useGetSettingsQuery,
} from "../../data/useProfile";
import TabIcon from "../shared/TabIcon";
import Settings from "./settings/Settings";
import { InvestorTypes } from "../../data/types/investors";
import Snackbar, { SnackbarProvider } from "../shared/Snackbar";
import { ProfileContext } from "../../contexts/ProfileContext";
import KeycloakSettings from "./settings/KeycloakSettings";
import { AuthorizedMembersCard } from "./authorized-members/AuthorizedMembersCard";
import { MFEContext } from "../../contexts/MFEContext";
import { MIXPANEL_EVENTS } from "../../helpers/mixpanel";
import { useSearchParams } from "react-router-dom";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../data/dtos/profile-config.dto";
import { ErrorPage } from "../shared/ErrorPage";
import TaxCenterMFE from "./tax-center/TaxCenter";
import DocumentCenterMFE from "./document-center/DocumentCenter";
import { useQueryClient } from "@tanstack/react-query";
import { Documents } from "./documents/Documents";
import "../../scss/styles.scss";
import { CountriesProvider } from "../shared/CountryTextField/CountriesContext";
import DownloadModal from "./settings/components/DownloadModal";
import { KeyParties } from "./key-parties/KeyParties";
import MissingEntityType from "./MissingEntityType";
import EntityOnboardingCardContainer from "../shared/onboarding/EntityOnboardingCardContainer";
import { shouldShowEntityOnboarding } from "../../helpers/portfolio";
import { useGetOnboardingData } from "../../data/usePortfolio";
import { isItemResolved } from "../../utils/updatesRequiredResolution";
import { useGetActiveSessionsQuery } from "../../data/useAuth";

const TAB_PATHS = Object.values(tabRoutes);

const tabStyle = {
  textTransform: "none",
  minHeight: "48px",
  gap: "6px",
};

export const Profile = () => {
  const {
    investorType,
    fullName,
    entityName,
    investorUserEmail,
    verificationStatus,
  } = useUserInfo();
  const { mixPanel } = useContext(MFEContext);
  const {
    isKeycloakEnabled,
    basePath,
    additionalAccreditation,
    authorizedMembers,
  } = useContext(ProfileContext);

  const navigate = useNavigate();
  const [isAcceptEntityInviteInProgress, setIsAcceptEntityInviteInProgress] =
    useState(false);
  const pendingActionsQuery = useGetPendingActionsQuery();
  const configQuery = useGetConfigQuery();
  const onboardingQuery = useGetOnboardingData();

  const showLoading =
    pendingActionsQuery.isPending ||
    isAcceptEntityInviteInProgress ||
    configQuery.isPending;

  const isEntity = investorType === InvestorTypes.ENTITY;

  // Show entity onboarding card logic (shared with Portfolio and any surface).
  const showEntityOnboarding = shouldShowEntityOnboarding(
    verificationStatus,
    investorType,
  );

  const [queryParams, setQueryParams] = useSearchParams();
  const queryClient = useQueryClient();

  const authorizedAccountCode =
    queryParams.get("code") || queryParams.get("invite-token");

  const isDownloadInformationModalOpen = queryParams.get("download") === "true";

  const user = useUserInfo();

  const missingEntityType =
    user.investorType === "entity" && user.entityType === undefined;

  // Tab navigation handling
  const { tab } = useParams();

  const navigateToInformationOnPermission = useCallback(
    (tabPermission?: ProfilePermissionType) => {
      if (
        !tabPermission ||
        tabPermission === ProfilePermissionTypes.NO_ACCESS
      ) {
        navigate(profilePaths(basePath).information, { replace: true });
      }
    },
    [basePath, navigate],
  );

  /**
   * Check if the url comes from an entity invite
   * If so, it will approve the invite and navigate to the information tab (default tab)
   */
  const authorizeAccount = useCallback(
    (code: string) => {
      if (isAcceptEntityInviteInProgress) {
        return;
      }
      setIsAcceptEntityInviteInProgress(true);
      authorizedMembers
        .approveAuthorizedAccount(code)
        .then(() => {
          return queryClient.invalidateQueries({
            queryKey: ["user-investors"],
          });
        })
        .catch((error) => {
          console.error("Error accepting entity invite:", error);
        })
        .finally(() => {
          navigateToInformationOnPermission();
          setIsAcceptEntityInviteInProgress(false);
        });
    },
    [
      authorizedMembers,
      isAcceptEntityInviteInProgress,
      navigateToInformationOnPermission,
      queryClient,
    ],
  );

  // Capture paths that are not allowed or available and track tab access events
  useEffect(() => {
    if (!tab || !TAB_PATHS.includes(tab)) {
      navigateToInformationOnPermission();
      return;
    }

    if (!configQuery.data) {
      return;
    }

    switch (tab) {
      case tabRoutes.information:
        navigateToInformationOnPermission(
          configQuery.data.profile.information.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.INFORMATION_TAB_LOAD);
        break;
      case tabRoutes.authorizedMembers:
        if (authorizedAccountCode) {
          authorizeAccount(authorizedAccountCode);
          break;
        }
        navigateToInformationOnPermission(
          configQuery.data.profile.authorizedMembers.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.AUTHORIZED_MEMBERS_TAB_LOAD);
        break;
      case tabRoutes.taxCenter:
        navigateToInformationOnPermission(
          configQuery.data.profile.taxCenter.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.TAX_CENTER_TAB_LOAD);
        break;
      case tabRoutes.documents:
        navigateToInformationOnPermission(
          configQuery.data.profile.documents.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.DOCUMENTS_TAB_LOAD);
        break;
      case tabRoutes.investmentProfile:
        navigateToInformationOnPermission(
          configQuery.data.profile.investmentProfile.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.INVESTMENT_PROFILE_TAB_LOAD, {
          investorType,
        });
        break;
      case tabRoutes.documentCenter:
        navigateToInformationOnPermission(
          configQuery.data.profile.documentCenter.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.DOCUMENT_CENTER_TAB_LOAD);
        break;
      case tabRoutes.settings:
        navigateToInformationOnPermission(
          configQuery.data.profile.settings.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.SETTINGS_TAB_LOAD, {
          investorType,
        });
        break;
      case tabRoutes.keyParties:
        navigateToInformationOnPermission(
          configQuery.data.profile.settings.permission,
        );
        mixPanel.track(MIXPANEL_EVENTS.PROFILE.KEY_PARTIES_TAB_LOAD, {
          investorType,
        });
        break;
      default:
        break;
    }
  }, [
    tab,
    navigate,
    mixPanel,
    investorType,
    authorizeAccount,
    configQuery.data,
    navigateToInformationOnPermission,
    authorizedAccountCode,
    isAcceptEntityInviteInProgress,
  ]);

  // Handle tab clicks
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (TAB_PATHS.includes(newValue)) {
      navigate(tabRoutesToProfilePaths(basePath)[newValue]);
    }
  };

  const userName =
    (investorType === "individual" ? fullName : entityName) ||
    investorUserEmail;

  // Preload
  useGetInvestorInformationQuery({
    enabled: Boolean(
      configQuery.data &&
        configQuery.data.profile.information.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });
  const documentsQuery = useGetDocumentsQuery({
    enabled: Boolean(
      isEntity &&
        !missingEntityType &&
        configQuery.data &&
        configQuery.data.profile.documents.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });
  useGetKeyPartiesQuery({
    enabled: Boolean(
      isEntity &&
        !missingEntityType &&
        configQuery.data &&
        configQuery.data.profile.keyParties.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });
  useGetInvestmentProfileQuery({
    enabled: Boolean(
      configQuery.data &&
        configQuery.data.profile.investmentProfile.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });
  useGetSettingsQuery({
    enabled: Boolean(
      configQuery.data &&
        configQuery.data.profile.settings.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });
  useGetActiveSessionsQuery({
    enabled: Boolean(
      isKeycloakEnabled &&
        configQuery.data &&
        configQuery.data.profile.settings.permission !==
          ProfilePermissionTypes.NO_ACCESS,
    ),
  });

  // Reflects unresolved operator-flagged updates (ON-2759), in addition to
  // the generic pendingAction signal from GET /profile/pending-actions.
  // Computed above the configQuery.error early return (even though nothing
  // currently sits between them) so this can never accidentally end up after
  // a future hook and violate the Rules of Hooks.
  const uploadedDocumentIds = new Set(
    documentsQuery.data?.uploadedDocuments.map((doc) => doc.documentId) ?? [],
  );
  const hasUnresolvedDocumentComment = (
    onboardingQuery.data?.requestedUpdatesGrouped?.documentComments ?? []
  ).some(({ documentId }) => uploadedDocumentIds.has(documentId));
  const hasUnresolvedKeyPartyComment = (
    onboardingQuery.data?.requestedUpdatesGrouped?.keyPartyComments ?? []
  ).some(
    ({ legalSignerId }) =>
      !isItemResolved(
        user.investorId,
        legalSignerId,
        onboardingQuery.data?.requestedUpdatesGrouped?.requestedAt,
      ),
  );

  if (configQuery.error) {
    return (
      <ErrorPage
        errorCode={configQuery.error.correlationId}
        onRefresh={configQuery.refetch}
      />
    );
  }

  return (
    <WhmStack gap="16px">
      <WhmTypography variant="h1">
        <Translate translationKey={TranslationKeys.PROFILE_TITLE} />
      </WhmTypography>
      <WhmGrid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        sx={{ display: { xs: "block", xl: "none" } }}
      >
        <WhmTypography variant="h5" data-test-id="profile-user-name">
          {userName}
        </WhmTypography>
      </WhmGrid>

      {showEntityOnboarding && (
        <EntityOnboardingCardContainer compressed={true} />
      )}

      {showLoading ? (
        <div
          id="tabs-skeleton"
          data-test-id="profile-tabs-skeleton"
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
          }}
        >
          <WhmSkeleton variant="rounded" width={210} height={50} />
          <WhmSkeleton variant="rounded" width={141} height={50} />
          <WhmSkeleton variant="rounded" width={191} height={50} />
          <WhmSkeleton variant="rounded" width={125} height={50} />
        </div>
      ) : (
        <WhmTabs
          value={tab || tabRoutes.information}
          onChange={handleTabChange}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="information-tabs"
          sx={{
            display: "flex",
          }}
        >
          {configQuery.data.profile.information.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={
                <TabIcon
                  icon={
                    <PersonalInformationIcon
                      width={24}
                      height={24}
                      fill="currentColor"
                    />
                  }
                  hasBadge={
                    pendingActionsQuery.data!.profile.personalInformation
                      .pendingAction &&
                    configQuery.data.profile.information.permission !==
                      ProfilePermissionTypes.READ_ONLY
                  }
                  badgeTestId="profile-personal-information-badge"
                />
              }
              data-test-id="profile-personal-information-tab"
              iconPosition="start"
              value={tabRoutes.information}
              label={
                <Translate
                  translationKey={
                    isEntity
                      ? TranslationKeys.PROFILE_ENTITY_INFORMATION_TAB_TITLE
                      : TranslationKeys.PROFILE_PERSONAL_INFORMATION_TAB_TITLE
                  }
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.documents.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={
                <TabIcon
                  icon={
                    <DocumentsIcon width={24} height={24} fill="currentColor" />
                  }
                  hasBadge={
                    (pendingActionsQuery.data?.profile.documents
                      .pendingAction ||
                      hasUnresolvedDocumentComment) &&
                    configQuery.data.profile.documents.permission !==
                      ProfilePermissionTypes.READ_ONLY
                  }
                  badgeTestId="profile-documents-badge"
                />
              }
              iconPosition="start"
              data-test-id="profile-documents-tab"
              value={tabRoutes.documents}
              label={
                <Translate
                  translationKey={TranslationKeys.PROFILE_DOCUMENTS_TAB_TITLE}
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.keyParties.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={
                <TabIcon
                  icon={
                    <KeyPartiesIcon
                      width={24}
                      height={24}
                      fill="currentColor"
                    />
                  }
                  hasBadge={
                    (pendingActionsQuery.data?.profile.keyParties
                      .pendingAction ||
                      hasUnresolvedKeyPartyComment) &&
                    configQuery.data.profile.keyParties.permission !==
                      ProfilePermissionTypes.READ_ONLY
                  }
                  badgeTestId="profile-key-parties-badge"
                />
              }
              iconPosition="start"
              data-test-id="profile-key-parties-tab"
              value={tabRoutes.keyParties}
              label={
                <Translate
                  translationKey={TranslationKeys.PROFILE_KEY_PARTIES_TAB_TITLE}
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.authorizedMembers.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={<MembersIcon width={24} height={24} fill="currentColor" />}
              iconPosition="start"
              data-test-id="authorized-members-tab"
              value={tabRoutes.authorizedMembers}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_AUTHORIZED_MEMBERS_TAB_TITLE
                  }
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.taxCenter.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={
                <TabIcon
                  icon={<TaxIcon width={24} height={24} fill="currentColor" />}
                  hasBadge={
                    pendingActionsQuery.data?.profile.taxCenter.pendingAction &&
                    configQuery.data.profile.taxCenter.permission !==
                      ProfilePermissionTypes.READ_ONLY
                  }
                  badgeTestId="profile-tax-center-badge"
                />
              }
              iconPosition="start"
              data-test-id="profile-tax-center-tab"
              value={tabRoutes.taxCenter}
              label={
                <Translate
                  translationKey={TranslationKeys.PROFILE_TAX_CENTER_TAB_TITLE}
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.investmentProfile.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={
                <TabIcon
                  icon={
                    <InvestmentProfileIcon
                      width={24}
                      height={24}
                      fill="currentColor"
                    />
                  }
                  hasBadge={
                    pendingActionsQuery.data?.profile.investmentProfile
                      .pendingAction &&
                    configQuery.data.profile.investmentProfile.permission !==
                      ProfilePermissionTypes.READ_ONLY
                  }
                  badgeTestId="profile-investment-profile-badge"
                />
              }
              iconPosition="start"
              data-test-id="profile-investment-profile-tab"
              value={tabRoutes.investmentProfile}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_INVESTMENT_PROFILE_TAB_TITLE
                  }
                />
              }
              sx={tabStyle}
            />
          )}
          {Boolean(
            configQuery.data.profile.documentCenter.permission !==
              ProfilePermissionTypes.NO_ACCESS &&
              config.FEATURE_FLAGS.PROFILE_DOCUMENT_CENTER_TAB_ENABLED,
          ) && (
            <WhmTab
              icon={
                <TabIcon
                  icon={
                    <DocumentCenterIcon
                      width={24}
                      height={24}
                      fill="currentColor"
                    />
                  }
                />
              }
              iconPosition="start"
              data-test-id="profile-document-center-tab"
              value={tabRoutes.documentCenter}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_DOCUMENT_CENTER_TAB_TITLE
                  }
                />
              }
              sx={tabStyle}
            />
          )}
          {configQuery.data.profile.settings.permission !==
            ProfilePermissionTypes.NO_ACCESS && (
            <WhmTab
              icon={<SettingsIcon width={24} height={24} fill="currentColor" />}
              iconPosition="start"
              data-test-id="profile-settings-tab"
              value={tabRoutes.settings}
              label={
                <Translate
                  translationKey={TranslationKeys.PROFILE_SETTINGS_TAB_TITLE}
                />
              }
              sx={tabStyle}
            />
          )}
        </WhmTabs>
      )}

      {configQuery.data && (
        <>
          {tab === tabRoutes.information && (
            <Information
              isPendingActionsLoading={showLoading}
              permission={configQuery.data.profile.information.permission}
            />
          )}

          {tab === tabRoutes.documents &&
            isEntity &&
            (missingEntityType ? (
              <MissingEntityType />
            ) : (
              <Documents
                isPendingActionsLoading={showLoading}
                permission={configQuery.data.profile.documents.permission}
              />
            ))}

          {tab === tabRoutes.keyParties &&
            isEntity &&
            (missingEntityType ? (
              <MissingEntityType />
            ) : (
              <KeyParties
                isPendingActionsLoading={showLoading}
                permission={configQuery.data.profile.keyParties.permission}
                canDelete={Boolean(
                  configQuery.data.profile.keyParties.canDelete ?? true,
                )}
              />
            ))}

          {tab === tabRoutes.authorizedMembers && isEntity && (
            <AuthorizedMembersCard
              isPendingActionsLoading={
                showLoading || isAcceptEntityInviteInProgress
              }
              permission={configQuery.data.profile.authorizedMembers.permission}
            />
          )}

          {tab?.includes(tabRoutes.taxCenter) && (
            <TaxCenterMFE
              // eslint-disable-next-line
              refetchPendingActions={pendingActionsQuery.refetch}
              isPendingActionsLoading={showLoading}
              permission={configQuery.data.profile.taxCenter.permission}
            />
          )}

          {tab === tabRoutes.investmentProfile && (
            <InvestmentProfile
              additionalAccreditation={additionalAccreditation}
              isPendingActionsLoading={showLoading}
              permission={configQuery.data.profile.investmentProfile.permission}
            />
          )}

          {Boolean(
            tab?.includes(tabRoutes.documentCenter) &&
              config.FEATURE_FLAGS.PROFILE_DOCUMENT_CENTER_TAB_ENABLED,
          ) && (
            <DocumentCenterMFE
              // eslint-disable-next-line
              isPendingActionsLoading={showLoading}
              permission={configQuery.data.profile.documentCenter.permission}
            />
          )}

          {tab === tabRoutes.settings &&
            (isKeycloakEnabled ? (
              <KeycloakSettings
                isPendingActionsLoading={showLoading}
                permission={configQuery.data.profile.settings.permission}
              />
            ) : (
              <Settings
                isPendingActionsLoading={showLoading}
                permission={configQuery.data.profile.settings.permission}
              />
            ))}
        </>
      )}
      {isDownloadInformationModalOpen && (
        <DownloadModal
          open
          onClose={() => {
            const newQueryParams = new URLSearchParams(queryParams);
            newQueryParams.delete("download");
            setQueryParams(newQueryParams);
          }}
        />
      )}
    </WhmStack>
  );
};

export default function WrappedProfile() {
  return (
    <SnackbarProvider>
      <CountriesProvider>
        <Profile />
      </CountriesProvider>
      <Snackbar />
    </SnackbarProvider>
  );
}
